import { BehaviorSubject, Observable, interval, map, of, switchMap, timer } from 'rxjs';

import { Injectable } from '@angular/core';
import { ApiHelper, IHttpResult } from '@leandredev/common_ng_14';
import { Buffer } from 'buffer';
import { AuthenticationService } from '../authentication/authentication.service';
import { I_Claim, I_JWT } from 'src/app/utils/interfaces';
import { ProfileService } from '../profile/profile.service';
import { interfaces as I } from '@leandredev/dcbm-common-lib';
import { SessionService } from '../session/session.service';

import { MatSnackBar } from '@angular/material/snack-bar';
import { RefreshConnexionComponent } from 'src/app/modules/connexion/refresh-connexion.component';
import { Router } from '@angular/router';

const DISCONNECTION_COUNTDOWN = 60;

@Injectable()
export class SignInJwtService {
  protected _isConnecting = false;

  public get isConnecting(): boolean {
    return this._isConnecting;
  }

  readonly connected: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public get isConnected(): boolean {
    return this.connected.value;
  }

  constructor(
    private authentication: AuthenticationService,
    private profile: ProfileService,
    private session: SessionService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {
    if (this.session.isExpired()) {
      this.disconnect();
    } else {
      this.connect(this.session.value.token);
    }

    interval(1000)
      .pipe(
        map(() => {
          if (this.isConnected) {
            const jwt = this.session.value.token;

            const claim: I_Claim = this.parseJwt(jwt);

            this.openRefreshRequest(claim);

            if (this.tokenDuration(claim.exp) < 0) {
              timer(1500).subscribe(() => {
                this.disconnect();
                void this.router.navigateByUrl('/login');
              });
            }
          }
        })
      )
      .subscribe();
  }

  public refreshRequestOpened = false;

  public openRefreshRequest(claim: I_Claim): void {
    const tokenDuration = this.tokenDuration(claim.exp);

    if (!this.refreshRequestOpened && tokenDuration / 1000 <= DISCONNECTION_COUNTDOWN && tokenDuration / 1000 > 0) {
      this.refreshRequestOpened = true;

      this.snackBar
        .openFromComponent(RefreshConnexionComponent, {
          data: { refresh: () => this.refreshToken(), duration: tokenDuration },
          duration: tokenDuration
        })
        .afterDismissed()
        .subscribe(() => {
          this.refreshRequestOpened = false;
        });
    }
  }

  private parseJwt(jwt: string): I_Claim {
    return JSON.parse(Buffer.from(jwt.split('.')[1], 'base64').toString('utf-8')) as I_Claim;
  }

  protected _token: string;

  public token(): string {
    return this._token;
  }

  public connect(jwt: string): Observable<void> {
    if (this._isConnecting) throw new Error('Connection is started');

    if (!jwt) throw new Error('No token');

    if (this.isConnected) this.disconnect();

    this._isConnecting = true;

    const claim: I_Claim = this.parseJwt(jwt);

    this.session.patchSession(jwt, claim.sub, claim);

    this.profile.updateProfile(claim);

    this._token = jwt;

    this.connected.next(true);
    this._isConnecting = false;

    return of(null);
  }

  public disconnect(): void {
    this.snackBar.dismiss();
    if (this._isConnecting) throw new Error('Connexion is started');

    this.session.clearSession();
    this.profile.clearProfile();

    this.connected.next(false);
  }

  public refreshToken(): void {
    if (!this.isConnected) return;

    this.authentication
      .refreshToken({}, this.session.value.token)
      .pipe(
        ApiHelper.resultToObj('Authentication Error: refresh token'),
        map((token) => {
          if (!token) return this.disconnect();
          this._token = token.token;
          this.session.patchSession(token.token, this.session.value.userId);
        })
      )
      .subscribe({
        error: (err: Error) => {
          console.warn(err);
          this.disconnect();
        }
      });
  }

  private tokenDuration(timeSpan: number): number {
    return timeSpan * 1000 - Date.now();
  }

  public signInByLogin(email: string, password: string): Observable<void> {
    const signinRequest: I.ISigninRequest = { login: email, password, device: '' };

    return this.authentication.signIn(signinRequest, {}).pipe(
      ApiHelper.resultToObjWEmptyError('Error when sign in'),
      switchMap((res: I_JWT) => {
        return this.connect(res.jwt);
      })
    );
  }

  public lostPassword(email: string, url: string): Observable<IHttpResult<void>> {
    return this.authentication.lostPassword<void, I.IlostPasswordRequest>({ email, redirectUrl: url }, {});
  }

  public resetPassword(resetPasswordRequest: I.IresetPasswordRequest): Observable<any> {
    return this.authentication
      .resetPassword(resetPasswordRequest, {})
      .pipe(ApiHelper.resultToObjWEmptyError('Erreur de mise à jour du mot de passe'));
  }
}
